import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  LogOut,
  Lock,
} from 'lucide-react';

const AdminSidebar = ({
  admin,
  onLogout,
  sidebarOpen,
  setSidebarOpen,
  newLeadsCount = 0,
  showUserInfo = true,
}) => {
  const location = useLocation();

  const isActive = (path, { prefix = false } = {}) => {
    if (prefix) {
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    }
    return location.pathname === path;
  };

  const linkClass = (path, options = {}) =>
    `flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
      isActive(path, options)
        ? 'text-[#1A1A1A] bg-[#F5F3EF]'
        : 'text-[#6B6B6B] hover:bg-[#F5F3EF]'
    }`;

  return (
    <aside
      className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-[#E0DED8]
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
    >
      <div className="p-6 border-b border-[#E0DED8] hidden lg:block">
        <img src="/byron-logo-transparent-26.svg" alt="Byrōn" className="h-6 mb-1" />
        <p className="text-xs uppercase tracking-[0.15em] text-[#9B9B9B] mt-1">Admin Panel</p>
      </div>

      <nav className="p-4 space-y-1">
        <Link
          to="/admin/dashboard"
          className={linkClass('/admin/dashboard')}
          data-testid="nav-dashboard"
          onClick={() => setSidebarOpen?.(false)}
        >
          <LayoutDashboard size={18} />
          <span className="text-sm">Dashboard</span>
        </Link>
        <Link
          to="/admin/properties"
          className={linkClass('/admin/properties', { prefix: true })}
          data-testid="nav-properties"
          onClick={() => setSidebarOpen?.(false)}
        >
          <Building2 size={18} />
          <span className="text-sm">Properties</span>
        </Link>
        <Link
          to="/admin/leads"
          className={linkClass('/admin/leads', { prefix: true })}
          data-testid="nav-leads"
          onClick={() => setSidebarOpen?.(false)}
        >
          <Users size={18} />
          <span className="text-sm">Leads</span>
          {newLeadsCount > 0 && (
            <span className="ml-auto bg-[#8B7355] text-white text-xs px-2 py-0.5 rounded-full">
              {newLeadsCount}
            </span>
          )}
        </Link>
        <Link
          to="/admin/site-access"
          className={linkClass('/admin/site-access', { prefix: true })}
          data-testid="nav-site-access"
          onClick={() => setSidebarOpen?.(false)}
        >
          <Lock size={18} />
          <span className="text-sm">Site Access</span>
        </Link>
      </nav>

      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E0DED8]">
        {showUserInfo && admin && (
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#8B7355] rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {admin.name?.charAt(0) || 'A'}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-[#1A1A1A]">{admin.name}</p>
              <p className="text-xs text-[#9B9B9B]">{admin.email}</p>
            </div>
          </div>
        )}
        <button
          onClick={onLogout}
          className="flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors"
          data-testid="logout-button"
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
